import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { documentsAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorAlert from './ErrorAlert';
import { FiUpload, FiX, FiDownload } from 'react-icons/fi';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const PhotosTab = ({ requestId }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const { data: documentsData, isLoading, error, refetch } = useQuery(
    ['documents', requestId],
    () => documentsAPI.getDocuments(requestId),
    { enabled: !!requestId, staleTime: 30000 }
  );

  // Filter to only show image documents
  const photos = (documentsData?.data || []).filter(doc =>
    doc.mime_type?.startsWith('image/') || doc.document_type === 'photo'
  );

  const handleUpload = async (files) => {
    if (!requestId) return;
    setUploading(true);

    const fileArray = Array.from(files);
    for (const file of fileArray) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`"${file.name}" exceeds the 10 MB file size limit.`);
        continue;
      }
      if (!file.type.startsWith('image/')) {
        alert(`"${file.name}" is not an image file.`);
        continue;
      }
      try {
        await documentsAPI.uploadDocument(requestId, file, 'photo', file.name);
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }

    setUploading(false);
    refetch();
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await documentsAPI.deleteDocument(requestId, documentId);
      refetch();
      if (selectedPhoto?.id === documentId) setSelectedPhoto(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) handleUpload(e.dataTransfer.files);
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message="Failed to load photos" />;

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Photos</h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Receiving photos, dock images, and other visual documentation for this request.
        </p>
      </div>

      {/* Upload zone */}
      <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
            isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <p className="text-sm text-gray-600 mb-2">Drag and drop images here, or click to browse</p>
          <p className="text-xs text-gray-400 mb-2">Max 10 MB per file. JPG, PNG, GIF accepted.</p>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }}
            className="hidden"
            id={`photos-upload-${requestId}`}
          />
          <label
            htmlFor={`photos-upload-${requestId}`}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 cursor-pointer"
          >
            <FiUpload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : 'Upload Photos'}
          </label>
        </div>
      </div>

      {/* Photo grid */}
      <div className="border-t border-gray-200">
        {photos.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className="text-sm text-gray-500">No photos uploaded yet.</p>
          </div>
        ) : (
          <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {photos.map((photo) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                requestId={requestId}
                onView={() => setSelectedPhoto(photo)}
                onDelete={() => handleDelete(photo.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <Lightbox
          photo={selectedPhoto}
          requestId={requestId}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </div>
  );
};

const PhotoCard = ({ photo, requestId, onView, onDelete }) => {
  const [imgSrc, setImgSrc] = useState(null);

  // Load signed URL for the thumbnail
  React.useEffect(() => {
    documentsAPI.getDocumentUrl(requestId, photo.id)
      .then(res => setImgSrc(res.url))
      .catch(() => setImgSrc(null));
  }, [requestId, photo.id]);

  return (
    <div className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
      <div className="aspect-square flex items-center justify-center cursor-pointer" onClick={onView}>
        {imgSrc ? (
          <img src={imgSrc} alt={photo.original_name} className="w-full h-full object-cover" />
        ) : (
          <div className="text-gray-400 text-xs">Loading...</div>
        )}
      </div>
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-end">
        <div className="w-full p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="text-white text-xs truncate">{photo.original_name}</p>
          <div className="flex justify-end mt-1">
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-white hover:text-red-300 text-xs" aria-label="Delete photo">
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Lightbox = ({ photo, requestId, onClose }) => {
  const [imgSrc, setImgSrc] = useState(null);

  React.useEffect(() => {
    documentsAPI.getDocumentUrl(requestId, photo.id)
      .then(res => setImgSrc(res.url))
      .catch(() => setImgSrc(null));
  }, [requestId, photo.id]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white hover:text-gray-300"
          aria-label="Close"
        >
          <FiX className="w-6 h-6" />
        </button>
        {imgSrc ? (
          <img src={imgSrc} alt={photo.original_name} className="max-w-full max-h-[85vh] rounded-lg" />
        ) : (
          <div className="text-white">Loading...</div>
        )}
        <p className="text-white text-sm mt-2 text-center">{photo.original_name}</p>
      </div>
    </div>
  );
};

export default PhotosTab;
